import React, { useState } from 'react';

const TYPE_LABEL = {
  drone_attack:        'DRONE ATTACK',
  missile_attack:      'MISSILE',
  piracy:              'PIRACY',
  armed_robbery:       'ARMED ROBBERY',
  hijack_attempt:      'HIJACK ATTEMPT',
  vessel_seizure:      'VESSEL SEIZURE',
  piracy_approach:     'PIRACY APPROACH',
  suspicious_approach: 'SUSPICIOUS APPROACH',
  sighting:            'SIGHTING',
  missile_sighting:    'MISSILE SIGHTING',
  vessel_harassment:   'HARASSMENT',
  mine_warning:        'MINE WARNING',
  attack:              'ATTACK',
  incident:            'INCIDENT',
};

const TYPE_COLOR = {
  drone_attack:        '#E11D48',
  missile_attack:      '#E11D48',
  piracy:              '#E11D48',
  armed_robbery:       '#E11D48',
  hijack_attempt:      '#E11D48',
  vessel_seizure:      '#E11D48',
  attack:              '#E11D48',
  piracy_approach:     '#F59E0B',
  suspicious_approach: '#F59E0B',
  sighting:            '#F59E0B',
  missile_sighting:    '#F59E0B',
  vessel_harassment:   '#F59E0B',
  mine_warning:        '#EAB308',
  incident:            '#F59E0B',
};

function relTime(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const h    = Math.floor(diff / 3600000);
  if (h < 1)   return 'just now';
  if (h < 24)  return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
  }) + ' UTC';
}

export default function IncidentRow({ incident }) {
  const [expanded, setExpanded] = useState(false);

  const color      = TYPE_COLOR[incident.type] || '#F59E0B';
  const typeLabel  = TYPE_LABEL[incident.type]  || incident.type?.replace(/_/g, ' ').toUpperCase() || 'INCIDENT';
  const dateStr    = incident.date || incident.published;
  const sourceName = incident.primary_source?.name || null;
  const coveredBy  = incident.also_covered_by || [];
  const summary    = incident.advisory_text || incident.summary || '';

  function openSearch() {
    const q = encodeURIComponent(incident.headline);
    window.open(`https://www.google.com/search?q=${q}`, '_blank', 'noopener,noreferrer');
  }

  return (
    <div style={{
      borderBottom: '1px solid #1e2d44',
      background: expanded ? '#0d1828' : 'transparent',
      transition: 'background .15s',
    }}>
      {/* ── Row header ── */}
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: '100%', textAlign: 'left', background: 'none', border: 'none',
          cursor: 'pointer', padding: '12px 16px',
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}
      >
        {/* Coloured dot */}
        <div style={{
          marginTop: 4, flexShrink: 0,
          width: 8, height: 8, borderRadius: '50%',
          background: color, boxShadow: `0 0 6px ${color}`,
        }} />

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Type badge + date */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700,
              color, letterSpacing: '.5px',
            }}>
              {typeLabel}
            </span>
            {dateStr && (
              <span style={{ fontSize: 11, color: '#8892a5', fontFamily: "'JetBrains Mono', monospace" }}>
                {formatDate(dateStr)}
              </span>
            )}
            <span style={{ fontSize: 11, color: '#8892a5' }}>·</span>
            <span style={{ fontSize: 11, color: '#8892a5' }}>{incident.zone || incident.zone_name}</span>
          </div>

          {/* Headline — plain text, not a link */}
          <div style={{ fontSize: 18, color: '#e2e8f0', fontWeight: 500, lineHeight: 1.4 }}>
            {incident.headline}
          </div>

          {/* Source attribution */}
          <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {sourceName && (
              <span style={{ fontSize: 11, color: '#8892a5' }}>
                via {sourceName}
                {dateStr && <span style={{ color: '#3a4558' }}> · {relTime(dateStr)}</span>}
              </span>
            )}
            {coveredBy.length > 0 && (
              <span style={{ fontSize: 11, color: '#3a4558' }}>
                · Also covered by {coveredBy.map(s => s.name).join(', ')}
              </span>
            )}
          </div>

          {/* Expand toggle */}
          <div style={{ fontSize: 11, color: '#3a4558', marginTop: 4, fontFamily: "'Figtree', sans-serif" }}>
            {expanded ? '▲ Hide detail' : '▼ Show detail'}
          </div>
        </div>
      </button>

      {/* ── Expanded content ── */}
      {expanded && (
        <div style={{ padding: '0 16px 16px 36px' }}>
          <div style={{
            background: '#0a1628', border: '1px solid #1e2d44', borderRadius: 8,
            padding: '14px 16px',
          }}>
            {/* Summary text */}
            {summary ? (
              <div style={{
                fontFamily: "'Figtree', sans-serif", fontSize: 12, color: '#c4ccd8',
                lineHeight: 1.7, whiteSpace: 'pre-wrap', marginBottom: 14,
              }}>
                {summary}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: '#3a4558', marginBottom: 14 }}>
                No summary available.
              </div>
            )}

            {/* Source + search button row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ fontSize: 11, color: '#3a4558', fontFamily: "'Figtree', sans-serif" }}>
                {sourceName ? `Source: ${sourceName}` : 'Source: Maritime news aggregator'}
                {coveredBy.length > 0 && ` · +${coveredBy.length} more`}
              </div>

              <button
                onClick={openSearch}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '5px 12px',
                  background: 'transparent',
                  border: '1px solid #2a3f5f',
                  borderRadius: 6,
                  color: 'var(--text-3, #8892a5)',
                  fontSize: 11,
                  fontFamily: "'Figtree', sans-serif",
                  cursor: 'pointer',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#3d5a80'; e.currentTarget.style.color = '#c4ccd8'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a3f5f'; e.currentTarget.style.color = 'var(--text-3, #8892a5)'; }}
              >
                <span>🔍</span> Search this incident
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
